import { useState } from "react";
import { Copy, Check } from "lucide-react";

type Props = {
  /** Text placed on the clipboard. */
  value: string;
  /** Accessible, unique description of what gets copied. */
  label: string;
  className?: string;
};

/**
 * One-click copy button. Falls back to a hidden textarea + execCommand on
 * browsers or insecure contexts where navigator.clipboard is unavailable.
 */
export function CopyButton({ value, label, className = "" }: Props) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? `${label} copied to clipboard` : `Copy ${label}`}
      className={`inline-flex items-center gap-1.5 border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-gold hover:text-gold ${className}`}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-gold" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

/** Labelled read-only row with its own copy control. */
export function CopyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[190px_1fr_auto] sm:items-start sm:gap-4 p-3 border-b border-border last:border-b-0">
      <span className="eyebrow">{label}</span>
      <span className="text-sm text-muted-foreground break-words">{value}</span>
      <CopyButton value={value} label={`${label.toLowerCase()} for this listing`} className="justify-self-start" />
    </div>
  );
}
